import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';

/**
 * CTAButton — reusable pill button with icon + label.
 *
 * Forwards a ref so a caller (e.g. DrawerCTAButtons) can pass this button's
 * DOM node as the trigger reference for a drilldown submenu-open action, the
 * same way SubMenuTrigger/HamburgerButton already do.
 *
 * @param {Object} props
 * @param {string} props.iconClass - The ddc-icon class for the button icon
 * @param {string} props.label - The button label text
 * @param {string} props.testId - The data-testid for the button
 * @param {() => void} [props.onClick] - Click handler
 * @param {boolean} [props.disabled] - Renders the native `disabled` attribute,
 *   visibly non-interactive rather than a silent no-op (e.g. while a caller
 *   depends on a fragment that hasn't finished loading yet)
 * @param {ElementRef} ref
 * @returns {React.ReactElement}
 */
const CTAButton = forwardRef(
	({ iconClass, label, testId, onClick, disabled }, ref) => (
		<button
			ref={ref}
			type="button"
			className="btn btn-default ws-nav-drawer__cta-btn d-flex justify-content-center align-items-center"
			data-testid={testId}
			onClick={onClick}
			disabled={disabled}
		>
			<span
				className={setClassNames(['ddc-icon', iconClass])}
				aria-hidden="true"
				data-testid={`${testId}-icon`}
			/>
			{label}
		</button>
	)
);

CTAButton.displayName = 'CTAButton';

CTAButton.propTypes = {
	iconClass: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	testId: PropTypes.string.isRequired,
	onClick: PropTypes.func,
	disabled: PropTypes.bool
};

CTAButton.defaultProps = {
	onClick: undefined,
	disabled: false
};

export default CTAButton;
